import {
  getRaceCountdownInfo,
  type RaceEventTimeLike
} from 'src/components/CrashChipPanel/raceCountdown'
import { DEFAULT_STREAM_PLAYOUT_DELAY_MS } from 'src/hooks/crashStreamConfig'
import type { HardCountdown } from 'src/utils/hardCloseClock'
import { puttsVisible, type PuttEntries } from './livePutts'
import type { PuttMark } from './tileViewModel'

export type BirdiePhase = 'idle' | 'open' | 'closing' | 'locked' | 'settled'

/**
 * The engine settles at least 3 s after the third putt comes to rest (A86), so
 * by the time `race_settled` lands the only thing still in flight is the
 * viewer's own playout buffer. Once that has elapsed the third ring is on screen
 * and the board may reveal the result. `RoundOdds.revealDelayS` is the
 * ENGINE-side hold and is already spent before `race_settled` — adding it here
 * would double-count.
 */
// 500 ms since 2026-09-23 (was the 1 s playout maximum). The engine now inserts every row
// 0.5 s after the frame LEAVES the encoder and settles 1 s after the third putt on the same
// clock, so this hold only has to cover the viewer's playout beyond that; at the old second
// the board trailed the ball by 2-3 s (owner). Wired viewers see the ball ~1 s after the
// encoder; the row shows 0.5 s (engine) + ~0.3 s (realtime) + 0.5 s (this) after it.
export const BIRDIE_RESULT_HOLD_MS = Math.min(500, DEFAULT_STREAM_PLAYOUT_DELAY_MS.max)

/**
 * The CSS ease the avatar ring and the arc hairline draw each tick's drain
 * with: `$birdie-t-stage` in `birdie-board.scss`. What is drawn trails the
 * value by this much, so the board computes the drain this far ahead
 * (`ringLeadMs`) and the stroke lands on the lock, and the between-rounds fill
 * on the next round. Change the two together.
 */
export const BIRDIE_RING_EASE_MS = 1150

export interface BoardPhaseInput {
  raceEvents: readonly RaceEventTimeLike[]
  nowMs: number
  bettingOpenWindowSec: number
  bettingClosingWindowSec: number
  settledWindowSec: number
  betsSubmitted: boolean
  /** The round's live per-putt rows (`store/birdiePutts`), keyed by putt. */
  putts?: PuttEntries
  /**
   * When THIS client first saw `race_settled`, on its own clock. The event's
   * timestamp is the server's, and `nowMs` is the browser's; subtracting one
   * from the other lets a fast viewer clock shorten the hold - the one
   * direction the hold must not fail in. Anchoring on the moment of receipt
   * makes the hold a pure duration on one clock. Absent for a round hydrated
   * from the store (already old), which falls back to the server stamp.
   */
  settledSeenAtMs?: number
  /**
   * A HARD round's betting count (hard close rev 4, DESIGN §6): the drain and
   * the seconds run to the board's lock, then over the short count of X, on
   * this page's own arrival clock. Absent or null: today's drain, from the
   * race_ready stamp.
   */
  hard?: HardCountdown | null
  /**
   * `performance.now()` at this render: the clock `hard`'s `startAt`/`endAt`
   * are on. Absent: the hard drain falls back to `hard.progress`.
   */
  hardNowMs?: number
  /**
   * How far ahead of `nowMs` the drain is computed, ms. The ring and the
   * hairline are eased toward each tick's value by a CSS transition, so what is
   * DRAWN trails the value by that transition; computing the value one ease
   * ahead lands the stroke on the lock (owner 10-29: "the timer around the
   * golfer avatar seems off"). 0 keeps the value exact for readers that draw it
   * without an ease.
   */
  ringLeadMs?: number
}

export interface BoardPhaseView {
  phase: BirdiePhase
  /** Whole seconds left, straight from getRaceCountdownInfo (1 Hz semantics). */
  secondsLeft: number
  /** 1 = full hairline, 0 = drained; ms precision so the stroke is smooth. */
  drain: number
  tilesLocked: boolean
  /**
   * A hard round past the board's lock (the short count of X to the cut). The
   * betting is over there even though `race_started` has not landed: the drain
   * is spent, the capsule takes the corner and the tee-off counts (owner 10-29).
   */
  hardLocked: boolean
  settlementVisible: boolean
  settledAtMs?: number
  /**
   * The rings the card may draw right now, in putt order — the received putts
   * whose own hold has elapsed, stopping at the first gap. Empty once
   * `settlementVisible`: the settled pattern from `market_results` is the
   * authority and OVERWRITES whatever the live events showed.
   */
  livePutts: PuttMark[]
}

const tsMs = (event: RaceEventTimeLike): number => new Date(event.eventTimestamp).getTime()

/** Non-mutating twin of raceUtils.getLatestEvent (which sorts its argument in place). */
export function latestEventType(events: readonly RaceEventTimeLike[]): string | undefined {
  let latest: RaceEventTimeLike | undefined
  for (const event of events) {
    if (latest === undefined || tsMs(event) > tsMs(latest)) latest = event
  }
  return latest?.eventType
}

const findEvent = (
  events: readonly RaceEventTimeLike[],
  eventType: string
): RaceEventTimeLike | undefined => events.find((event) => event.eventType === eventType)

export function puttResultVisible(input: { settledAt?: number; now: number }): boolean {
  if (input.settledAt === undefined || Number.isNaN(input.settledAt)) return false
  return input.now >= input.settledAt + BIRDIE_RESULT_HOLD_MS
}

/**
 * How long the host must wait before the reveal is due, ms — or `null` when
 * there is nothing to wait for (B11 item 7).
 *
 * The board's clock ticks at 1 Hz, so a `race_settled` that lands 10 ms after a
 * tick left the rings, the winner bloom and the total-win banner sitting until
 * the NEXT tick: the reveal arrived anywhere between the hold and the hold plus
 * one second, and the amount of lateness was pure luck. The host arms a
 * one-shot timer on this value instead, keyed on `settledAtMs`, so the reveal
 * lands ON the hold; the interval keeps running for everything else.
 *
 * `null` (never 0) for "already due" so the caller does not arm a timer that
 * fires on the next macrotask and re-renders for nothing.
 */
export function settlementRevealDelayMs(input: {
  settledAtMs?: number
  nowMs: number
}): number | null {
  if (input.settledAtMs === undefined || Number.isNaN(input.settledAtMs)) return null
  const delay = input.settledAtMs + BIRDIE_RESULT_HOLD_MS - input.nowMs
  return delay > 0 ? delay : null
}

export function boardPhase(input: BoardPhaseInput): BoardPhaseView {
  const windowSec = input.bettingOpenWindowSec + input.bettingClosingWindowSec
  const latest = latestEventType(input.raceEvents)

  const countdown = getRaceCountdownInfo({
    hard: input.hard,
    raceEvents: input.raceEvents,
    nowMs: input.nowMs,
    bettingWindowSec: windowSec,
    settledWindowSec: input.settledWindowSec
  })

  const settledEvent = findEvent(input.raceEvents, 'race_settled')
  const settledAtMs = settledEvent ? (input.settledSeenAtMs ?? tsMs(settledEvent)) : undefined

  let phase: BirdiePhase = 'idle'
  if (settledEvent) phase = 'settled'
  // `race_cancelled` is terminal — the engine emits it INSTEAD of
  // `race_settled` — so it has to lock the board like one. The round's prices
  // live on the `race_ready` payload and stay on the race row after the cancel,
  // which leaves `tilesLocked` as the only thing telling a cancelled round from
  // an open one: unlocked, the board keeps taking chips, and an armed auto-bet
  // rule that has not planned a race yet plans and places a whole set onto a
  // round that is over.
  else if (
    latest === 'race_started' ||
    latest === 'race_ended' ||
    latest === 'race_crashed' ||
    latest === 'race_cancelled'
  )
    phase = 'locked'
  else if (latest === 'race_starting') phase = 'closing'
  else if (latest === 'race_ready') phase = 'open'

  // The countdown ring only advances once per second (getRaceCountdownInfo floors
  // the elapsed seconds, raceCountdown.ts:129) — fine for a numeral, visibly
  // steppy for a 2-px hairline. Derive the stroke fraction ourselves.
  const leadMs = input.ringLeadMs ?? 0
  const hardLocked = (phase === 'open' || phase === 'closing') && input.hard?.stage === 'sending'
  let drain = 0
  if (hardLocked) {
    // The board has locked: the count of X belongs to the tee-off, not to a
    // second drain of the ring (which refilled and drained again before).
    drain = 0
  } else if ((phase === 'open' || phase === 'closing') && input.hard) {
    const { startAt, endAt, progress } = input.hard
    drain =
      input.hardNowMs !== undefined && endAt > startAt
        ? Math.max(0, Math.min(1, (endAt - (input.hardNowMs + leadMs)) / (endAt - startAt)))
        : Math.max(0, Math.min(1, 1 - progress / 100))
  } else if (phase === 'open' || phase === 'closing') {
    const readyEvent = findEvent(input.raceEvents, 'race_ready')
    if (readyEvent && windowSec > 0) {
      const elapsedMs = input.nowMs + leadMs - tsMs(readyEvent)
      const fraction = 1 - elapsedMs / (windowSec * 1000)
      drain = Math.max(0, Math.min(1, fraction))
    } else {
      drain = 1
    }
  } else if (phase === 'idle') {
    drain = 1
  }

  const settlementVisible = puttResultVisible({ settledAt: settledAtMs, now: input.nowMs })

  return {
    phase,
    secondsLeft: countdown.current,
    drain,
    tilesLocked: input.betsSubmitted || phase === 'locked' || phase === 'settled',
    hardLocked,
    settlementVisible,
    settledAtMs,
    livePutts: settlementVisible
      ? []
      : puttsVisible({ putts: input.putts, nowMs: input.nowMs, holdMs: BIRDIE_RESULT_HOLD_MS })
  }
}

/**
 * B40 — IS THE BOARD SEATED? (tipped into the scene.)
 *
 * Owner, watching it fire on the first putt: "maybe it should be applied when
 * the round actually starts, seems to happen late when the first ball is about
 * to drop?" — which it was. It rode `liveStatus.active`, and that is idle until
 * a putt row is on SCREEN, so the table tipped in at the last possible moment
 * instead of before the player had settled in to watch.
 *
 * The beat is the PHASE, and specifically the window the tee-off countdown
 * already occupies (`teeOff.ts`, B37): betting closes, `race_started` lands, the
 * engine plays its cutscene, the count runs, the clip begins. The board tips in
 * at the top of that window, so the first putt lands on a board that is already
 * seated and the two read as one moment.
 *
 * The three edges, all of them deliberate:
 *  - `betsSubmitted` is NOT this. `tilesLocked` goes true the moment a slip is
 *    in, which can be most of a betting window early; nothing is being played
 *    then and the board must stay flat.
 *  - a CANCELLED round never reaches a settle, so it is named outright. Without
 *    it the board would sit tipped through the whole inter-round gap and only
 *    fall flat as the next round's prices arrived.
 *  - the settled window lets go when the RESULT IS ON SCREEN, not when
 *    `race_settled` lands: the clip is still running out the last putt in
 *    between, and the win pill, the shockwave and the payout all land on a board
 *    that is already flat.
 */
export function boardSeated(input: {
  phase: BirdiePhase
  latestType?: string
  settlementVisible: boolean
  /** `boardPhase.hardLocked`: on a hard round the tee-off window opens at the lock. */
  hardLocked?: boolean
}): boolean {
  if (input.latestType === 'race_cancelled') return false
  if (input.phase === 'locked' || input.hardLocked === true) return true
  if (input.phase === 'settled') return !input.settlementVisible
  return false
}
